import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import AccordionBlock from './accordionBlock';
import { siteLogoLink } from './utils/Logo';

/**
 * Renders out the footer.
 * @return {JSX}
 */
export default function Footer() {
  const year = new Date().getFullYear();
  let internalSites: any = [];
  let usefulLinks: any = [];

  if (process.env.GATSBY_INTERNAL_SITES) {
    try {
      internalSites = JSON.parse(process.env.GATSBY_INTERNAL_SITES as any);
    } catch (err) {
      console.error('error formating internal sites', process.env.GATSBY_INTERNAL_SITES);
    }
  }

  if (process.env.GATSBY_USEFUL_LINKS) {
    try {
      usefulLinks = JSON.parse(process.env.GATSBY_USEFUL_LINKS as any);
    } catch (err) {
      console.error('error formating useful links', process.env.GATSBY_USEFUL_LINKS);
    }
  }

  const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          brandName
          logo
          buildVersion
          socialPlatforms {
            facebook
            linkedin
            twitter
            youtube
            instagram
            tiktok
          }
        }
      }
    }
  `);

  const GTiSitesContent = (
    <ul className="space-y-5 leading-4 pt-5">
      {internalSites.map((item: any, index: number) => {
        return (
          <li key={`footer_internal_link_${index}`}>
            <a
              href={item.href}
              target="_blank"
              rel="noreferrer"
              className="font-medium hover:text-primary-1100 text-xs text-gray line-clamp-1"
              aria-label={item.label}
            >
              {item.label}
            </a>
          </li>
        );
      })}
    </ul>
  );

  const footerNavs = [
    {
      title: 'Legal',
      content: (
        <ul className="space-y-5 leading-4 pt-5">
          <li>
            <a
              href="https://groupgti.com/privacy/gtimedia"
              className="font-medium hover:text-primary-1100 text-xs text-gray"
              aria-label="Privacy policy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy policy
            </a>
          </li>
          <li>
            <a
              href="https://groupgti.com/terms/advertisers"
              className="font-medium hover:text-primary-1100 text-xs text-gray"
              aria-label="Terms and Conditions for employers"
              target="_blank"
              rel="noreferrer"
            >
              Terms and conditions for employers
            </a>
          </li>
          <li>
            <a
              href="https://groupgti.com/terms/students"
              className="font-medium hover:text-primary-1100 text-xs text-gray"
              aria-label="Terms and Conditions for students"
              target="_blank"
              rel="noreferrer"
            >
              Terms and conditions for students
            </a>
          </li>
          <li>
            <a
              href="https://groupgti.com/about-our-cookies"
              className="font-medium hover:text-primary-1100 text-xs text-gray"
              aria-label="About our cookies"
              target="_blank"
              rel="noreferrer"
            >
              About our cookies
            </a>
          </li>
          <li>
            <button
              className="btn-link font-medium hover:text-primary-1100 text-xs text-gray text-left"
              onClick={() => {
                // eslint-disable-next-line no-undef
                cookiefirst_show_settings();
              }}
            >
              Manage cookie settings
            </button>
          </li>
          <li>
            <a
              href="https://groupgti.com/accessibility"
              className="font-medium hover:text-primary-1100 text-xs text-gray"
              aria-label="Accessibility"
              target="_blank"
              rel="noreferrer"
            >
              Accessibility
            </a>
          </li>
        </ul>
      )
    },
    {
      title: 'GTI Sites',
      content: GTiSitesContent
    },
    {
      title: 'Useful Links',
      content: (
        <ul className="space-y-5 leading-4 pt-5">
          {usefulLinks.map((item: any, index: number) => {
            if (item.to) {
              return (
                <li key={`footer_useful_link_${index}`}>
                  <Link
                    to={item.to}
                    className="font-medium hover:text-primary-1100 text-xs text-gray"
                    aria-label={item.label}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            } else {
              return (
                <li key={`footer_useful_link_${index}`}>
                  <a
                    href={item.href}
                    target="_blank"
                    className="font-medium hover:text-primary-1100 text-xs text-gray"
                    aria-label={item.label}
                    rel="noreferrer"
                  >
                    {item.label}
                  </a>
                </li>
              );
            }
          })}
        </ul>
      )
    },
    {
      title: 'Follow Us',
      content: (
        <ul className="mt-4 leading-4 pt-5 md:mt-0 space-x-7 md:space-y-5 md:space-x-0">
          {data.site.siteMetadata.socialPlatforms.facebook && (
            <li className="inline-block md:block">
              <a
                href={data.site.siteMetadata.socialPlatforms.facebook}
                target="_blank"
                className="font-medium text-2xl md:text-xs hover:text-primary-1100 text-gray"
                aria-label="Facebook"
                rel="noreferrer"
              >
                <FontAwesomeIcon icon={['fab', 'facebook-f']} />
                <span className="hidden md:inline-block text-xs ml-4 font-medium">Facebook</span>
              </a>
            </li>
          )}
          {data.site.siteMetadata.socialPlatforms.linkedin && (
            <li className="inline-block md:block">
              <a
                href={data.site.siteMetadata.socialPlatforms.linkedin}
                target="_blank"
                className="font-medium text-2xl md:text-xs hover:text-primary-1100 text-gray"
                aria-label="LinkedIn"
                rel="noreferrer"
              >
                <FontAwesomeIcon icon={['fab', 'linkedin-in']} />
                <span className="hidden md:inline-block text-xs ml-4 font-medium">LinkedIn</span>
              </a>
            </li>
          )}
          {data.site.siteMetadata.socialPlatforms.twitter && (
            <li className="inline-block md:block">
              <a
                href={data.site.siteMetadata.socialPlatforms.twitter}
                target="_blank"
                className="font-medium text-2xl md:text-xs hover:text-primary-1100 text-gray"
                aria-label="Twitter"
                rel="noreferrer"
              >
                <FontAwesomeIcon icon={['fab', 'x-twitter']} />
                <span className="hidden md:inline-block text-xs ml-4 font-medium">Twitter</span>
              </a>
            </li>
          )}
          {data.site.siteMetadata.socialPlatforms.youtube && (
            <li className="inline-block md:block">
              <a
                href={data.site.siteMetadata.socialPlatforms.youtube}
                target="_blank"
                className="font-medium text-2xl md:text-xs hover:text-primary-1100 text-gray"
                aria-label="YouTube"
                rel="noreferrer"
              >
                <FontAwesomeIcon icon={['fab', 'youtube']} />
                <span className="hidden md:inline-block text-xs ml-4 font-medium">YouTube</span>
              </a>
            </li>
          )}
          {data.site.siteMetadata.socialPlatforms.instagram && (
            <li className="inline-block md:block">
              <a
                href={data.site.siteMetadata.socialPlatforms.instagram}
                target="_blank"
                className="font-medium text-2xl md:text-xs hover:text-primary-1100 text-gray"
                aria-label="Instagram"
                rel="noreferrer"
              >
                <FontAwesomeIcon icon={['fab', 'instagram']} />
                <span className="hidden md:inline-block text-xs ml-4 font-medium">Instagram</span>
              </a>
            </li>
          )}
          {data.site.siteMetadata.socialPlatforms.tiktok && (
            <li className="inline-block md:block">
              <a
                href={data.site.siteMetadata.socialPlatforms.tiktok}
                target="_blank"
                className="font-medium text-2xl md:text-xs hover:text-primary-1100 text-gray"
                aria-label="TikTok"
                rel="noreferrer"
              >
                <FontAwesomeIcon icon={['fab', 'tiktok']} />
                <span className="hidden md:inline-block text-xs ml-4 font-medium">TikTok</span>
              </a>
            </li>
          )}
        </ul>
      )
    }
  ];

  return (
    <footer className="section-container-full border-t border-gray-200">
      <div className="section-container-2xl pb-5 md:pb-10">
        <footer className="container max-w-6xl pt-8 md:pt-12">
          <div className="md:flex lg:max-w-6xl border-gray-200 border-b">
            <div className="md:w-28 mb-7 md:mb-0 md:mt-1">{siteLogoLink(data, '', 'h-8', true)}</div>
            <div className="border-t border-grey-200 md:border-none divide-solid divide-y md:divide-y-0 md:gap-12 md:grid md:grid-cols-4 pb-4 md:pb-0">
              {footerNavs.map((footerNav: any, index: number) => {
                return (
                  <React.Fragment key={`footer-${index}`}>
                    <div className="md:hidden">
                      <AccordionBlock
                        content={[
                          {
                            question: footerNav.title,
                            answer: footerNav.content
                          }
                        ]}
                        indent={false}
                        containerClasses="my-4 !px-0"
                        accordionItemClasses="border-none py-0 px-0 shadow-none rounded-none bg-white"
                        questionClasses="text-sm uppercase tracking-widest text-gray hover:text-primary-1100 font-bold md:font-medium"
                      />
                    </div>
                    <div className="hidden md:block md:pb-8">
                      <h4
                        className="uppercase text-gray text-sm font-bold w-full flex justify-between items-start tracking-widest md:border-0
                              md:after:block md:after:empty md:after:h-0.5 md:after:w-7 md:after:bg-current md:block cursor-default"
                      >
                        <span className="mb-1.5 block">{footerNav.title}</span>
                      </h4>
                      <div>{footerNav.content}</div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div className="lg:flex lg:justify-between lg:max-w-6xl md:mt-5">
            <div className="mt-5 md:mt-0">
              <div className="text-xs text-gray-700">
                &copy; Copyright {year} GTI Futures Ltd. Registered in England No. 2347472.
                <br />
                The Fountain Building, Howbery Park, Benson Lane, Wallingford, Oxfordshire OX10 8BA UK.
              </div>
            </div>
            <div className="block mt-5 md:mt-0">
              <div>
                <svg
                  className={`w-20 dark-host:fill-white`}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 33.7 11.67"
                  aria-labelledby="builtbygroupgti"
                  role="img"
                >
                  <title id="builtbygroupgti">Built by GTI</title>
                  <defs></defs>
                  <g id="Layer_2" data-name="Layer 2">
                    <g id="Layer_1-2" data-name="Layer 1">
                      <path
                        className="cls-1"
                        d="M26.1,6.9a.89.89,0,0,0,.66-.26A.93.93,0,0,0,27,6,.91.91,0,0,0,26.09,5a1,1,0,0,0-.66,1.6.89.89,0,0,0,.67.26M27,4.57h1.3V3.63L29,3.55l.05,0v1H30V5.1H29V6.66c0,.35.15.53.44.53A.47.47,0,0,0,29.8,7l.06,0,.19.42,0,0a1,1,0,0,1-.66.24,1,1,0,0,1-.74-.28,1.07,1.07,0,0,1-.28-.8V5.1h-.73V7.22a1.43,1.43,0,0,1-.45,1.11A1.73,1.73,0,0,1,26,8.69a1.82,1.82,0,0,1-.83-.17,1.48,1.48,0,0,1-.61-.47V8l.33-.35h0A1.28,1.28,0,0,0,26,8.16a1,1,0,0,0,.69-.23A.82.82,0,0,0,27,7.29V7.07a1.41,1.41,0,0,1-1,.37A1.43,1.43,0,0,1,24.52,6V6a1.43,1.43,0,0,1,.42-1A1.46,1.46,0,0,1,26,4.49a1.38,1.38,0,0,1,1,.43Z"
                      />
                      <path
                        className="cls-1"
                        d="M31.14,7.7h-.66V4.57h.66Zm-.33-3.65a.43.43,0,0,1-.32-.14.48.48,0,0,1-.14-.34.47.47,0,0,1,.14-.33.42.42,0,0,1,.32-.13.48.48,0,0,1,.47.47.49.49,0,0,1-.14.34.47.47,0,0,1-.33.13"
                      />
                      <path
                        className="cls-1"
                        d="M28,0A5.79,5.79,0,0,1,33.7,5.84a5.74,5.74,0,1,1-11.48,0A5.79,5.79,0,0,1,28,0ZM22.84,5.84a5.12,5.12,0,1,0,10.24,0,5.12,5.12,0,1,0-10.24,0Z"
                      />
                      <path
                        className="cls-1"
                        d="M3,6.13a.92.92,0,0,1,.21.63.87.87,0,0,1-.37.76,1.79,1.79,0,0,1-1.08.27H0V3.94H1.66a1.7,1.7,0,0,1,1,.25A.86.86,0,0,1,3,4.93a1,1,0,0,1-.15.54.94.94,0,0,1-.43.33A1.07,1.07,0,0,1,3,6.13ZM.41,4.27v1.4H1.63a1.19,1.19,0,0,0,.72-.18A.6.6,0,0,0,2.61,5a.6.6,0,0,0-.26-.52,1.19,1.19,0,0,0-.72-.18Zm2.14,3a.61.61,0,0,0,.26-.54c0-.49-.35-.73-1-.73H.41V7.45H1.77A1.41,1.41,0,0,0,2.55,7.27Z"
                      />
                      <path
                        className="cls-1"
                        d="M6.67,4.89v2.9H6.3V7.26a1.13,1.13,0,0,1-.43.41,1.22,1.22,0,0,1-.61.14,1.25,1.25,0,0,1-.9-.31A1.26,1.26,0,0,1,4,6.57V4.89h.39V6.53a1,1,0,0,0,.22.7.89.89,0,0,0,.65.23A.94.94,0,0,0,6,7.18a1.09,1.09,0,0,0,.26-.77V4.89Z"
                      />
                      <path
                        className="cls-1"
                        d="M7.76,4.18A.3.3,0,0,1,7.68,4a.27.27,0,0,1,.08-.19A.26.26,0,0,1,8,3.71a.28.28,0,0,1,.21.08A.26.26,0,0,1,8.25,4a.3.3,0,0,1-.08.2A.32.32,0,0,1,8,4.26.3.3,0,0,1,7.76,4.18Zm0,.71h.39v2.9H7.77Z"
                      />
                      <path className="cls-1" d="M9.25,3.71h.39V7.79H9.25Z" />
                      <path
                        className="cls-1"
                        d="M12.26,7.61a.59.59,0,0,1-.27.15,1,1,0,0,1-.34,0A.77.77,0,0,1,10.8,7V5.22h-.52V4.89h.52V4.26h.39v.63h.88v.33h-.88V7a.57.57,0,0,0,.13.39.5.5,0,0,0,.37.14.67.67,0,0,0,.24,0,.59.59,0,0,0,.19-.11Z"
                      />
                      <path
                        className="cls-1"
                        d="M16.64,5.06a1.22,1.22,0,0,1,.51.52,1.45,1.45,0,0,1,.19.76,1.45,1.45,0,0,1-.19.76,1.3,1.3,0,0,1-.51.53,1.53,1.53,0,0,1-.74.18,1.38,1.38,0,0,1-.65-.15,1.19,1.19,0,0,1-.47-.45v.58h-.37V3.71h.39V5.44A1.19,1.19,0,0,1,15.27,5a1.3,1.3,0,0,1,.63-.15A1.43,1.43,0,0,1,16.64,5.06Zm-.22,2.27a1,1,0,0,0,.39-.4,1.31,1.31,0,0,0,0-1.18,1,1,0,0,0-.39-.4,1.15,1.15,0,0,0-1.1,0,1,1,0,0,0-.38.4,1.24,1.24,0,0,0,0,1.18,1,1,0,0,0,.38.4,1.15,1.15,0,0,0,1.1,0Z"
                      />
                      <path
                        className="cls-1"
                        d="M20.49,4.89,19,8.13a1.35,1.35,0,0,1-.41.58.92.92,0,0,1-.55.17,1,1,0,0,1-.39-.07.8.8,0,0,1-.32-.19l.19-.29a.73.73,0,0,0,.52.22.53.53,0,0,0,.34-.11,1,1,0,0,0,.26-.38l.13-.28L17.52,4.89h.4L19,7.35,20.1,4.89Z"
                      />
                    </g>
                  </g>
                </svg>
              </div>
              <p className="md:text-right text-xs pt-1">{'v' + data.site.siteMetadata.buildVersion}</p>
            </div>
          </div>
        </footer>
      </div>
    </footer>
  );
}
